import { classnames } from '@/helpers';

const gradientBase = 'bg-surface_l4 animate-pulse';

/**
 * Skeleton that mirrors the structure of the Reviews module (header, score summary,
 * rating breakdown bars, review cards, pagination) while reviews are loading.
 */
export const ReviewsSkeleton = ({
  withStars,
  withPagination,
  withHeader,
  withList,
  ratingRows = 5,
  reviewCards = 2,
}: {
  withStars?: boolean;
  withPagination?: boolean;
  withHeader?: boolean;
  withList?: boolean;
  ratingRows?: number;
  reviewCards?: number;
}) => {
  return (
    <div aria-hidden="true" className="gap-sm relative flex flex-col">
      {/* Header: title + info icon, time filter dropdown */}
      {withHeader ? (
        <header className="mb-xs flex items-center justify-between">
          <div className="gap-sm flex items-center">
            <div className={classnames(gradientBase, 'h-lg w-[140px] rounded')} />
            <div className={classnames(gradientBase, 'h-lg w-lg rounded-full')} />
          </div>
          <div className={classnames(gradientBase, 'h-2xl w-[120px] rounded-lg')} />
        </header>
      ) : null}

      <div className="gap-2xl flex flex-col">
        {/* Score summary: "4.7 av 5" + stars */}
        {withStars ? (
          <>
            <div className="flex items-center justify-between">
              <div className="gap-sm flex items-end">
                <div className={classnames(gradientBase, 'h-4xl w-4xl rounded')} />
                <div className={classnames(gradientBase, 'h-lg w-xl rounded')} />
                <div className={classnames(gradientBase, 'h-4xl w-lg rounded')} />
              </div>
              <div className={classnames(gradientBase, 'h-lg w-[140px] rounded')} />
            </div>
            {/* Rating breakdown bars */}
            <div className="flex flex-col">
              {Array.from({ length: ratingRows }, (_, i) => ratingRows - i).map((rating) => (
                <div key={rating} className="gap-sm flex h-[36px] items-center">
                  <div className={classnames(gradientBase, 'h-sm w-[70px] shrink-0 rounded')} />
                  <div className={classnames(gradientBase, 'h-[4px] flex-1 rounded-sm')} />
                  <div className={classnames(gradientBase, 'h-sm w-xl shrink-0 rounded')} />
                </div>
              ))}
            </div>
          </>
        ) : null}

        {/* Review cards */}
        {withList ? (
          <div className="gap-md flex flex-col">
            {Array.from({ length: reviewCards }, (_, key) => key).map((key) => (
              <div key={key} className="bg-bg_secondary p-xl gap-md flex w-full flex-col rounded-lg">
                <div className="gap-md flex items-center">
                  <div className={classnames(gradientBase, 'h-[48px] w-[48px] shrink-0 rounded-sm')} />
                  <div className="gap-xs flex flex-1 flex-col">
                    <div className={classnames(gradientBase, 'h-sm w-[100px] rounded')} />
                    <div className={classnames(gradientBase, 'h-xs w-[90px] rounded')} />
                    <div className={classnames(gradientBase, 'h-sm w-[130px] rounded')} />
                  </div>
                </div>
                <div className={classnames(gradientBase, 'h-sm w-9/12 rounded')} />
              </div>
            ))}
          </div>
        ) : null}

        {/* Pagination */}
        {withPagination ? (
          <div className="flex items-center justify-between">
            <div className={classnames(gradientBase, 'h-3xl w-3xl rounded-full')} />
            <div className={classnames(gradientBase, 'h-sm w-[110px] rounded')} />
            <div className={classnames(gradientBase, 'h-3xl w-3xl rounded-full')} />
          </div>
        ) : null}
      </div>
    </div>
  );
};
